import { ethers } from 'ethers';
import {
  APERTUM_ADD_CHAIN_PARAMS,
  APERTUM_CHAIN_ID,
  APERTUM_CHAIN_ID_HEX,
  parseChainId,
} from './apertumChain';

export interface Eip1193Provider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
}

export interface DiscoveredWallet {
  id: string;
  name: string;
  rdns?: string;
  provider: Eip1193Provider;
}

export async function requestAccounts(provider: Eip1193Provider): Promise<string> {
  const accounts = (await provider.request({ method: 'eth_requestAccounts' })) as string[];
  if (!accounts?.[0]) throw new Error('No wallet account returned.');
  return ethers.utils.getAddress(accounts[0]);
}

export async function readChainId(provider: Eip1193Provider): Promise<number | null> {
  const raw = await provider.request({ method: 'eth_chainId' });
  return parseChainId(raw);
}

export async function switchToApertum(provider: Eip1193Provider): Promise<void> {
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: APERTUM_CHAIN_ID_HEX }],
    });
  } catch (err: unknown) {
    const code = (err as { code?: number })?.code;
    if (code === 4902) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [APERTUM_ADD_CHAIN_PARAMS],
      });
      return;
    }
    throw err;
  }
}

export async function signPersonalMessage(
  provider: Eip1193Provider,
  address: string,
  message: string
): Promise<string> {
  const hex = ethers.utils.hexlify(ethers.utils.toUtf8Bytes(message));
  const sig = await provider.request({
    method: 'personal_sign',
    params: [hex, address],
  });
  if (typeof sig !== 'string') throw new Error('Wallet did not return a signature.');
  return sig;
}

export function recoverPersonalSigner(message: string, signature: string): string {
  return ethers.utils.verifyMessage(message, signature);
}

export function discoverInjectedWallets(): DiscoveredWallet[] {
  const found = new Map<string, DiscoveredWallet>();

  const onAnnounce = (event: Event) => {
    const detail = (event as CustomEvent).detail as
      | { info?: { uuid?: string; name?: string; rdns?: string }; provider?: Eip1193Provider }
      | undefined;
    if (!detail?.provider || !detail.info?.uuid) return;
    found.set(detail.info.uuid, {
      id: detail.info.uuid,
      name: detail.info.name || 'Wallet',
      rdns: detail.info.rdns,
      provider: detail.provider,
    });
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('eip6963:announceProvider', onAnnounce as EventListener);
    window.dispatchEvent(new Event('eip6963:requestProvider'));
    window.removeEventListener('eip6963:announceProvider', onAnnounce as EventListener);

    const injected = (window as unknown as { ethereum?: Eip1193Provider }).ethereum;
    if (injected && found.size === 0) {
      found.set('injected', { id: 'injected', name: 'Browser Wallet', provider: injected });
    }
  }

  return [...found.values()];
}

export function isApertumChain(chainId: number | null): boolean {
  return chainId === APERTUM_CHAIN_ID;
}
